import { Box, Grid } from '@chakra-ui/react';
import {
  ReactZoomPanPinchRef,
  TransformComponent,
  TransformWrapper
} from '@pronestor/react-zoom-pan-pinch';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  calcZoomScaleForWidth,
  nearStation,
  revertX,
  revertY,
  translateX,
  translateY,
  VBHeight_ZoomMap,
  VBWidth_ZoomMap
} from '../common/svg';
import Controls from '../components/organisms/Controls';
import ZoomMap_Map from '../components/organisms/ZoomMap_Map';
import { useZoomState } from '../hooks/useZoomState';
import { WindowSizeContext } from '../context/WindowSizeContextProvider';
import { MasterContext } from '../context/MasterContextProvider';
import { URLs } from '../RouterConfig';
import Message from '../components/organisms/Message';

const DEFAULT_STATION_NO = '27';

const ZoomMap: React.FC = () => {
  const zoomRef = useRef<ReactZoomPanPinchRef>(null);
  const navigate = useNavigate();
  const { master } = useContext(MasterContext);
  const { size } = useContext(WindowSizeContext);

  // 電停が画面内にちょうど収まるズーム倍率
  const zoomScale = useMemo(
    () =>
      calcZoomScaleForWidth(size, VBWidth_ZoomMap, VBHeight_ZoomMap, 140, 20),
    [size]
  );
  const { zoomState, setZoommState } = useZoomState(zoomScale, zoomScale, 0, 0);

  // 初期フォーカス位置
  const { stationNo } = useParams<{ stationNo: string }>();

  const initialPositionX = useMemo(
    () =>
      translateX(
        size,
        master.stations.find((station) => station.station_no === stationNo)
          ?.x_zoom ??
          master.stations.find(
            (station) => station.station_no === DEFAULT_STATION_NO
          )?.x_zoom ??
          0,
        VBWidth_ZoomMap,
        VBHeight_ZoomMap,
        zoomScale
      ),
    [master.stations, size, stationNo, zoomScale]
  );

  const initialPositionY = useMemo(
    () =>
      translateY(
        size,
        master.stations.find((station) => station.station_no === stationNo)
          ?.y_zoom ??
          master.stations.find(
            (station) => station.station_no === DEFAULT_STATION_NO
          )?.y_zoom ??
          0,
        VBWidth_ZoomMap,
        VBHeight_ZoomMap,
        zoomScale
      ),
    [master.stations, size, stationNo, zoomScale]
  );

  // ズーム操作時にzoomStateを更新する関数
  const updateZoomState = useCallback(() => {
    setZoommState({
      scale: zoomRef.current?.state.scale ?? zoomScale,
      previousScale: zoomRef.current?.state.previousScale ?? zoomScale,
      positionX: zoomRef.current?.state.positionX ?? 0,
      positionY: zoomRef.current?.state.positionY ?? 0
    });
  }, [setZoommState, zoomScale]);

  // 拡大率が一定を下回ったときに遷移
  useEffect(() => {
    if (zoomState.scale < zoomScale) {
      const _stationNo = nearStation(
        revertX(
          zoomState.positionX,
          size,
          VBWidth_ZoomMap,
          VBHeight_ZoomMap,
          zoomState.scale
        ),
        revertY(
          zoomState.positionY,
          size,
          VBWidth_ZoomMap,
          VBHeight_ZoomMap,
          zoomState.scale
        ),
        master.stations,
        'zoom'
      );
      navigate(`${URLs.overview}`, { state: { stationNo: _stationNo } });
    }
  }, [
    master.stations,
    navigate,
    size,
    zoomScale,
    zoomState.positionX,
    zoomState.positionY,
    zoomState.scale
  ]);

  return (
    <>
      <TransformWrapper
        minScale={1}
        initialScale={zoomScale}
        initialPositionX={initialPositionX}
        initialPositionY={initialPositionY}
        maxScale={zoomScale}
        ref={zoomRef}
        onZoom={updateZoomState}
      >
        {({ setTransform }) => (
          <Grid
            w={size.w}
            h={size.h}
            gridTemplateAreas={`'header' 'map' 'footer'`}
            gridTemplateRows='5% 88% 7%'
          >
            <Box gridArea={'header'}>
              <Message></Message>
            </Box>
            <TransformComponent
              wrapperStyle={{
                width: '100%',
                height: '100%',
                backgroundColor: 'black',
                gridArea: 'map'
              }}
              contentStyle={{
                width: '100%',
                height: '100%'
              }}
            >
              <ZoomMap_Map></ZoomMap_Map>
            </TransformComponent>
            <Box gridArea={'footer'}>
              <Controls
                reset={() => {
                  navigate(`${URLs.overview}`);
                }}
                toCurrent={(stationNo) => {
                  setTransform(
                    translateX(
                      size,
                      master.stations.find(
                        (station) => station.station_no === stationNo
                      )?.x_zoom ?? 0,
                      VBWidth_ZoomMap,
                      VBHeight_ZoomMap,
                      zoomScale
                    ),
                    translateY(
                      size,
                      master.stations.find(
                        (station) => station.station_no === stationNo
                      )?.y_zoom ?? 0,
                      VBWidth_ZoomMap,
                      VBHeight_ZoomMap,
                      zoomScale
                    ),
                    zoomScale
                  );
                }}
              ></Controls>
            </Box>
          </Grid>
        )}
      </TransformWrapper>
    </>
  );
};

export default React.memo(ZoomMap);
